import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { isDevtoolsEnabled } from './utils/isDevtoolsEnabled'

// Default level configuration matching backend (XP values in cents, same as backend)
const DEFAULT_LEVEL_CONFIG = [
  { level: 0, minXP: 0, multiplierBonus: 0 },
  { level: 1, minXP: 50_000, multiplierBonus: 0.1 }, // $500
  { level: 2, minXP: 250_000, multiplierBonus: 0.2 }, // $2,500
  { level: 3, minXP: 750_000, multiplierBonus: 0.3 }, // $7,500
  { level: 4, minXP: 2_000_000, multiplierBonus: 0.4 }, // $20,000
  { level: 5, minXP: 5_000_000, multiplierBonus: 0.5 }, // $50,000
  { level: 6, minXP: 12_500_000, multiplierBonus: 0.6 }, // $125,000
  { level: 7, minXP: 30_000_000, multiplierBonus: 0.7 }, // $300,000
  { level: 8, minXP: 60_000_000, multiplierBonus: 0.8 }, // $600,000
  { level: 9, minXP: 100_000_000, multiplierBonus: 0.9 }, // $1,000,000
  { level: 10, minXP: 150_000_000, multiplierBonus: 1.0 } // $1,500,000
]

export interface PlayerLevelInfo {
  level: number
  xp: number // XP in cents (displayed as unitless XP number)
  multiplierBonus: number
}

export interface PlayerStats {
  totalBets: number
  totalWins: number
  winRate: number // Percentage 0-100
  // Currency-converted values from backend (in player's session currency, minor units)
  totalWagered?: number // In minor currency units (e.g., cents, satang)
  totalWon?: number // Total amount won in minor currency units
  biggestWin?: number // In minor currency units
  currency?: string // Player's session currency code (e.g., "USD", "THB")
}

interface PlayerLevelState {
  playerLevel: PlayerLevelInfo | null
  playerStats: PlayerStats | null
  levelSystemEnabled: boolean
}

export interface PlayerLevelActions {
  setPlayerLevel: (info: PlayerLevelInfo) => void
  setPlayerStats: (stats: PlayerStats) => void
  setLevelSystemEnabled: (enabled: boolean) => void
  clearPlayerLevel: () => void
}

type PlayerLevelStore = PlayerLevelState & { actions: PlayerLevelActions }

// Helper to get next level info
export const getNextLevelInfo = (currentLevel: number) => {
  const nextTier = DEFAULT_LEVEL_CONFIG.find((t) => t.level === currentLevel + 1)
  if (!nextTier) return null
  return {
    level: nextTier.level,
    minXP: nextTier.minXP,
    multiplierBonus: nextTier.multiplierBonus
  }
}

// Helper to get current level tier
export const getCurrentLevelTier = (level: number) => {
  return DEFAULT_LEVEL_CONFIG.find((t) => t.level === level) ?? DEFAULT_LEVEL_CONFIG[0]
}

export const usePlayerLevelStore = create<PlayerLevelStore>()(
  devtools(
    persist(
      (set) => ({
        playerLevel: null,
        playerStats: null,
        levelSystemEnabled: false,
        actions: {
          setPlayerLevel: (info: PlayerLevelInfo) => {
            set({ playerLevel: info }, undefined, 'setPlayerLevel')
          },
          setPlayerStats: (stats: PlayerStats) => {
            set({ playerStats: stats }, undefined, 'setPlayerStats')
          },
          setLevelSystemEnabled: (enabled: boolean) => {
            set({ levelSystemEnabled: enabled }, undefined, 'setLevelSystemEnabled')
          },
          clearPlayerLevel: () => {
            set({ playerLevel: null, playerStats: null }, undefined, 'clearPlayerLevel')
          }
        }
      }),
      {
        name: 'player-level-storage',
        partialize: (state) => ({
          playerLevel: state.playerLevel,
          playerStats: state.playerStats,
          levelSystemEnabled: state.levelSystemEnabled
        })
      }
    ),
    { name: 'PlayerLevelStore', enabled: isDevtoolsEnabled() }
  )
)

export const useGetPlayerLevel = () => usePlayerLevelStore((state) => state.playerLevel)
export const useGetPlayerStats = () => usePlayerLevelStore((state) => state.playerStats)
export const useIsLevelSystemEnabled = () =>
  usePlayerLevelStore((state) => state.levelSystemEnabled)
export const usePlayerLevelActions = () => usePlayerLevelStore((state) => state.actions)
